import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, BarChart3 } from 'lucide-react';

interface CookieConsentProps {
  onAccept: (analytics: boolean, ads: boolean) => void;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    // Verificar si ya se ha dado consentimiento
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({ analytics, ads, timestamp: Date.now() }));
    onAccept(analytics, ads);
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    setAnalytics(true);
    setAds(true);
    localStorage.setItem('cookie-consent', JSON.stringify({ analytics: true, ads: true, timestamp: Date.now() }));
    onAccept(true, true);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    setAnalytics(false);
    setAds(false);
    localStorage.setItem('cookie-consent', JSON.stringify({ analytics: false, ads: false, timestamp: Date.now() }));
    onAccept(false, false);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-neuro-blue shadow-2xl">
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
          {/* Icono y título */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Cookie className="w-6 h-6 text-neuro-blue" />
              <Shield className="w-5 h-5 text-neuro-green" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Configuración de Cookies</h3>
              <p className="text-sm text-gray-600">Respetamos tu privacidad</p>
            </div>
          </div>

          {/* Descripción */}
          <div className="flex-1 text-sm text-gray-700">
            <p>
              Utilizamos cookies para mejorar tu experiencia, analizar el tráfico del sitio y personalizar el contenido. 
              Puedes elegir qué tipos de cookies aceptar.
            </p>
          </div>

          {/* Configuración de cookies */}
          <div className="flex flex-col gap-3 w-full lg:w-auto">
            {/* Analytics */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="w-4 h-4 text-neuro-blue bg-gray-100 border-gray-300 rounded focus:ring-neuro-blue focus:ring-2"
              />
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-neuro-blue" />
                <span className="text-sm font-medium">Analytics</span>
              </div>
            </label>

            {/* Publicidad */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={ads}
                onChange={(e) => setAds(e.target.checked)}
                className="w-4 h-4 text-neuro-blue bg-gray-100 border-gray-300 rounded focus:ring-neuro-blue focus:ring-2"
              />
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-neuro-green" />
                <span className="text-sm font-medium">Publicidad</span>
              </div>
            </label>
          </div>

          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
            <button
              onClick={handleRejectAll}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Rechazar Todo
            </button>
            <button
              onClick={handleAccept}
              className="px-4 py-2 text-sm font-medium text-white bg-neuro-blue border border-neuro-blue rounded-lg hover:bg-neuro-dark-blue transition-colors"
            >
              Guardar Preferencias
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-4 py-2 text-sm font-medium text-white bg-neuro-green border border-neuro-green rounded-lg hover:bg-green-700 transition-colors"
            >
              Aceptar Todo
            </button>
          </div>
        </div>

        {/* Información adicional */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-xs text-gray-500">
              Al continuar navegando, aceptas nuestro uso de cookies. 
              <a href="/politica-privacidad" className="text-neuro-blue hover:underline ml-1">
                Política de Privacidad
              </a>
            </p>
            <button
              onClick={() => setIsVisible(false)}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors self-end sm:self-auto"
            >
              <X className="w-3 h-3" />
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
